import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useCogsBreakdown } from '@/api/cogs'
import type { CogsBreakdownData, CogsLine, CogsPart } from '@/api/cogs'
import { formatBps } from '@/components/cogs/percent'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney, formatQty } from '@/components/materials/pool-format'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

// COGS breakdown (UI-SCREENS C-02, 08c #204): one Variant's stored variant_cogs row, read only —
// one row per BOM line (material per color slot, machine, Component), labor and the rounding
// line, which sum to the total; then per Model part the data window (Batches of any version of the
// Model, each with its version, ADR 0015; Σ OK and the window's yield, #204 Q1 / Q3) or the
// estimate inputs. No recompute button: the job
// recomputes on every input change (#204 Q2). Owner and Staff; cost figures only.
export const Route = createFileRoute('/w/$slug/cogs/$variantId')({
  component: CogsBreakdownScreen,
  staticData: { breadcrumb: 'cogs.breakdown.title' },
})

const decimals = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 4 })
const money2 = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 })
const formatComputedAt = (iso: string) =>
  new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso))
const formatDate = (iso: string) => new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium' }).format(new Date(iso))
const formatYield = (ok: number, printed: number) =>
  printed > 0 ? `${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 }).format((ok / printed) * 100)}%` : '—'

function CogsBreakdownScreen() {
  const { t } = useTranslation()
  const { slug, variantId } = Route.useParams()
  const query = useCogsBreakdown(slug, Number(variantId))

  if (query.isPending) return <LoadingSkeleton variant="page" />
  if (query.isError || !query.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const { data } = query

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/cogs" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('cogs.breakdown.back')}
      </Link>
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-semibold">{t('cogs.breakdown.title')}</h1>
        <Link
          to="/w/$slug/variants/$variantId"
          params={{ slug, variantId: String(data.id) }}
          className="underline-offset-4 hover:underline"
        >
          {data.product.name} · {data.name} <span className="text-sm text-muted-foreground">{data.sku}</span>
        </Link>
      </div>

      {data.cogs ? (
        <>
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">{t('cogs.breakdown.total')}</span>
              <span className="text-2xl font-semibold">Rp {formatMoney(data.cogs.total)}</span>
              {data.cogs.estimated ? <Badge variant="secondary">{t('cogs.estimated')}</Badge> : null}
            </div>
            <p className="text-sm text-muted-foreground">
              {t('cogs.breakdown.computedAt', { at: formatComputedAt(data.cogs.computed_at) })}.{' '}
              {t('cogs.breakdown.autoRecompute')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('cogs.breakdown.bomFrom', { date: formatDate(data.cogs.breakdown.bom_effective_from) })}
            </p>
          </div>
          <Lines breakdown={data.cogs.breakdown} total={data.cogs.total} />
          <Window slug={slug} parts={data.cogs.breakdown.parts} />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{t('cogs.breakdown.notComputable')}</p>
      )}
    </section>
  )
}

function Lines({ breakdown, total }: { breakdown: CogsBreakdownData; total: number }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('cogs.breakdown.lines')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col divide-y">
          {breakdown.lines.map((line, index) => (
            <LineRow key={index} line={line} />
          ))}
          <Row title={t('cogs.breakdown.rounding')} detail={t('cogs.breakdown.roundingHint')} amount={breakdown.rounding} />
          <li className="flex items-baseline justify-between gap-4 pt-3 font-semibold">
            <span>{t('cogs.breakdown.total')}</span>
            <span className="whitespace-nowrap">Rp {formatMoney(total)}</span>
          </li>
        </ul>
      </CardContent>
    </Card>
  )
}

function LineRow({ line }: { line: CogsLine }) {
  const { t } = useTranslation()
  const qty = 'qty' in line && line.kind !== 'component' && line.qty !== 1 ? t('cogs.breakdown.qty', { qty: line.qty }) : null

  switch (line.kind) {
    case 'material':
      return (
        <Row
          title={t('cogs.breakdown.material', { model: line.label, slot: line.slot_key })}
          detail={t('cogs.breakdown.materialMath', {
            grams: decimals.format(line.grams_per_part),
            costPerGram: money2.format(line.cost_per_gram),
            pool: line.pool_label,
          })}
          extra={[
            line.grams_per_plate !== undefined ? t('cogs.breakdown.gramsPerPlate', { grams: line.grams_per_plate }) : null,
            qty,
          ]}
          amount={line.amount}
        />
      )
    case 'machine':
      return (
        <Row
          title={t('cogs.breakdown.machine', { model: line.label })}
          detail={t('cogs.breakdown.machineMath', {
            hours: decimals.format(line.hours_per_part),
            rate: money2.format(line.rate_per_hour),
          })}
          extra={[qty]}
          amount={line.amount}
        />
      )
    case 'component':
      return (
        <Row
          title={t('cogs.breakdown.component', { name: line.label })}
          detail={t('cogs.breakdown.componentMath', { qty: line.qty, cost: money2.format(line.cost_per_piece) })}
          amount={line.amount}
        />
      )
    case 'labor':
      return (
        <Row
          title={t('cogs.breakdown.labor')}
          detail={t('cogs.breakdown.laborMath', {
            minutes: decimals.format(line.minutes_per_unit),
            rate: formatMoney(line.labor_rate_per_hour),
          })}
          extra={[
            line.from_default
              ? t('cogs.breakdown.estimatedReasons.labor_default')
              : t('cogs.breakdown.laborWindow', { count: line.assemblies_used, minutes: line.minutes, units: line.units }),
          ]}
          amount={line.amount}
        />
      )
  }
}

function Row({
  title,
  detail,
  extra = [],
  amount,
}: {
  title: string
  detail: string
  extra?: (string | null)[]
  amount: number
}) {
  return (
    <li className="flex items-start justify-between gap-4 py-3 first:pt-0">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="font-medium">{title}</span>
        <span className="text-sm text-muted-foreground">{detail}</span>
        {extra.filter(Boolean).map((text) => (
          <span key={text} className="text-sm text-muted-foreground">
            {text}
          </span>
        ))}
      </div>
      <span className="whitespace-nowrap font-medium">Rp {formatMoney(amount)}</span>
    </li>
  )
}

function Window({ slug, parts }: { slug: string; parts: CogsPart[] }) {
  const { t } = useTranslation()
  if (parts.length === 0) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('cogs.breakdown.window')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {parts.map((part) => (
          <div key={part.model_id} className="flex flex-col gap-1 text-sm">
            <span className="font-medium">
              {t('cogs.breakdown.windowPart', { model: part.model_name, version: part.version_no })}
            </span>
            <span className="text-muted-foreground">
              {t('cogs.breakdown.windowStats', {
                batches: part.batches_used,
                ok: formatQty(part.parts_ok),
                printed: formatQty(part.parts_printed),
              })}
            </span>
            <span>{t('cogs.breakdown.yield', { percent: formatYield(part.parts_ok, part.parts_printed) })}</span>
            {part.estimated && part.estimate_reason ? (
              <>
                <span>
                  <Badge variant="secondary">{t(`cogs.breakdown.estimatedReasons.${part.estimate_reason}`)}</Badge>
                </span>
                <span className="text-muted-foreground">
                  {t('cogs.breakdown.estimateInputs', {
                    parts: part.parts_per_plate,
                    minutes: part.est_minutes_per_plate,
                    yield: formatBps(part.default_yield_bps),
                    rate: money2.format(part.estimate_rate ?? 0),
                  })}
                </span>
              </>
            ) : null}
            {part.batches.length > 0 ? (
              <ul className="flex flex-col gap-0.5">
                <li className="text-muted-foreground">{t('cogs.breakdown.batches')}</li>
                {part.batches.map((batch) => (
                  <li key={batch.id} className="flex flex-wrap gap-x-2">
                    <Link
                      to="/w/$slug/batches/$batchId"
                      params={{ slug, batchId: String(batch.id) }}
                      className="font-mono underline-offset-4 hover:underline"
                    >
                      {batch.number}
                    </Link>
                    <span className="text-muted-foreground">
                      {t('cogs.breakdown.batchRow', {
                        ok: batch.parts_ok,
                        printed: batch.parts_printed,
                        grams: formatQty(batch.grams),
                        minutes: batch.minutes,
                        machine: batch.machine_name,
                        version: batch.version_no,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
        <p className="text-sm text-muted-foreground">{t('cogs.breakdown.yieldNote')}</p>
      </CardContent>
    </Card>
  )
}
